import { isSteamClient } from './steam'
import {
  getLegacyQueryLanguage,
  getPathLanguage,
  withLanguagePath,
} from './url-language-utils'

const ARENA_HOSTS = new Set(['werewolf.mentiss.ai', 'b-werewolf.mentiss.ai'])

export function isHostedArenaHostname(hostname: string): boolean {
  return ARENA_HOSTS.has(hostname)
}

export interface ArenaNotificationSetupLocationInput {
  hostname: string
  search: string
  rememberedArena?: boolean
  steamClient?: boolean
}

export interface NotificationSettingsLocationInput {
  hostname: string
  language?: string
  pathname?: string
  steamClient?: boolean
}

export function isArenaNotificationSetupLocation({
  hostname,
  search,
  rememberedArena = false,
  steamClient = false,
}: ArenaNotificationSetupLocationInput): boolean {
  if (steamClient)
    return false
  if (isHostedArenaHostname(hostname))
    return true

  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1'
  if (!isLocalDev)
    return false

  return new URLSearchParams(search).has('arena') || rememberedArena
}

export function isArenaHost(): boolean {
  if (typeof window === 'undefined')
    return false
  if (isHostedArenaHostname(window.location.hostname))
    return true
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  if (isLocalDev && new URLSearchParams(window.location.search).has('research')) {
    try { sessionStorage.removeItem('arena') }
    catch {}
    return false
  }
  const hasArenaParam = new URLSearchParams(window.location.search).has('arena')
  if (hasArenaParam) {
    if (isLocalDev) {
      try { sessionStorage.setItem('arena', '1') }
      catch {}
    }
    return true
  }
  if (isLocalDev) {
    try { return sessionStorage.getItem('arena') === '1' }
    catch { return false }
  }
  return false
}

export function isArenaNotificationSetupHost(): boolean {
  if (typeof window === 'undefined')
    return false

  const { hostname, search } = window.location
  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1'
  let rememberedArena = false
  if (isLocalDev) {
    try { rememberedArena = sessionStorage.getItem('arena') === '1' }
    catch {}
  }

  return isArenaNotificationSetupLocation({
    hostname,
    search,
    rememberedArena,
    steamClient: isSteamClient(),
  })
}

export function arenaUrlForHostname(hostname: string): string | null {
  if (isHostedArenaHostname(hostname))
    return null
  if (hostname === 'mentiss.ai' || hostname === 'www.mentiss.ai')
    return 'https://werewolf.mentiss.ai'
  if (hostname === 'b-app.mentiss.ai' || hostname.startsWith('b-'))
    return 'https://b-werewolf.mentiss.ai'
  return null
}

export function getArenaUrl(): string | null {
  if (typeof window === 'undefined')
    return null
  if (isSteamClient())
    return null
  return arenaUrlForHostname(window.location.hostname)
}

export function getResearchPageUrl(path: string, language: string): string {
  if (typeof window === 'undefined')
    return `https://mentiss.ai${withLanguagePath(path, language)}`
  const { hostname, origin } = window.location
  const researchOrigin = hostname === 'b-werewolf.mentiss.ai'
    ? 'https://b-app.mentiss.ai'
    : hostname === 'werewolf.mentiss.ai'
      ? 'https://mentiss.ai'
      : origin
  const isLocalDev = hostname === 'localhost' || hostname === '127.0.0.1'
  return `${researchOrigin}${withLanguagePath(path, language)}${isLocalDev ? '?research' : ''}`
}

export function getResearchHomeUrl(language: string): string {
  return getResearchPageUrl('/', language)
}

export function buildSupportContactUrl(): string {
  if (typeof window === 'undefined')
    return 'https://mentiss.ai/en/contact-us'

  const { hostname, origin, pathname, search } = window.location
  const researchOrigin = hostname === 'b-werewolf.mentiss.ai'
    ? 'https://b-app.mentiss.ai'
    : hostname === 'werewolf.mentiss.ai'
      ? 'https://mentiss.ai'
      : origin
  const language = getPathLanguage(pathname)
    ?? getLegacyQueryLanguage(search)
    ?? 'en'
  const url = new URL(withLanguagePath('/contact-us', language), researchOrigin)
  return url.toString()
}

export function notificationSettingsUrlForLocation({
  hostname,
  language,
  pathname = '/',
  steamClient = false,
}: NotificationSettingsLocationInput): string | null {
  if (steamClient)
    return null
  const dashboardPath = withLanguagePath(
    '/dashboard',
    getPathLanguage(pathname) ?? language ?? 'en',
  )
  if (isHostedArenaHostname(hostname))
    return `${dashboardPath}?tab=notifications`

  const arenaUrl = arenaUrlForHostname(hostname)
  if (arenaUrl)
    return `${arenaUrl}${dashboardPath}?tab=notifications`

  if (hostname === 'localhost' || hostname === '127.0.0.1')
    return `${dashboardPath}?arena&tab=notifications`
  return null
}

export function buildNotificationSettingsUrl(
  language?: string,
): string | null {
  if (typeof window === 'undefined')
    return null
  return notificationSettingsUrlForLocation({
    hostname: window.location.hostname,
    language,
    pathname: window.location.pathname,
    steamClient: isSteamClient(),
  })
}

export function buildInviteUrl(code: string): string {
  if (typeof window === 'undefined')
    return withLanguagePath(`/room/${encodeURIComponent(code)}`, 'en')
  const language = getPathLanguage(window.location.pathname)
    ?? getLegacyQueryLanguage(window.location.search)
    ?? 'en'
  const path = withLanguagePath(`/room/${encodeURIComponent(code)}`, language)
  const arenaUrl = getArenaUrl()
  const url = new URL(path, arenaUrl ?? window.location.origin)
  if (!arenaUrl && !isHostedArenaHostname(window.location.hostname)
    && !isSteamClient()) {
    url.searchParams.set('arena', '')
  }
  return url.toString()
}

export function buildPublicPlayerProfileUrl(
  username: string,
  language?: string,
): string {
  const pathname = `/players/${encodeURIComponent(username.toLowerCase())}`
  const pathLanguage = typeof window === 'undefined'
    ? null
    : getPathLanguage(window.location.pathname)
  const path = withLanguagePath(pathname, pathLanguage ?? language ?? 'en')
  const arenaUrl = getArenaUrl()
  const needsArenaOverride = typeof window !== 'undefined'
    && !arenaUrl
    && !isHostedArenaHostname(window.location.hostname)
    && !isSteamClient()
  const query = needsArenaOverride ? ['arena'] : []
  const suffix = query.length > 0 ? `?${query.join('&')}` : ''
  return `${arenaUrl ?? ''}${path}${suffix}`
}
